import { ErrorStringFromException, isNullOrUndefined } from '../../utils/utils';
import { DocumentStorage } from '../office-js/DocumentStorage';
import { DocumentStorageError, DocumentStorageErrors } from '../office-js/DocumentStorageErrors';
import { MAX_MINUTES_WAIT_TIME_FOR_XML_STORAGE_OPERATION_MILLISECONDS, MAX_MINUTES_WAIT_TIME_FOR_XML_STORAGE_GET_NAMESPACE_PART_MILLISECONDS } from '../../AppConstants';
import { LogError } from '../../utils/ErrorTelemetry';
import { LogEventNames } from '../interfaces/ITelemetryLogger';
import { TranscriptionStorageType } from '../interfaces/IStoredTranscription';

const s_uri: string = 'http://schemas.microsoft.com/office/transcription/2022';
const s_storedTranscription: string = 'storedTranscription';
const s_xpathRootNodeXMLStorage = `/*[local-name()='${s_storedTranscription}' and namespace-uri()='${s_uri}']`;
const s_nameSpaceUriSchema = `<${s_storedTranscription} xmlns='${s_uri}'/>`;

/* <storedTranscription xmlns="http://schemas.microsoft.com/office/transcription/2022"> TRANSCRIPTION </storedTranscription> */

export class DocumentStorageWordXML extends DocumentStorage {

  constructor() {
    super(TranscriptionStorageType.DocumentXmlStorage);
  }

  public async WriteTranscribeContentToClient(content: string): Promise<void> {
    try {
      await this.SetContentToXMLPart(await this.FetchCustomXMLPart(), content);
    } catch (e) {
      throw new DocumentStorageError(DocumentStorageErrors.WriteContentToClientStorageFailed, ErrorStringFromException(e));
    }
  }

  public async DeleteTranscribeContentFromClient(): Promise<void> {
    try {
      const contentXMLParts: Office.CustomXmlPart[] = await this.getTranscriptionNamespacePart();
      const contentXmlPartsLength = isNullOrUndefined(contentXMLParts) ? 0 : contentXMLParts.length;
      for (let idx = 0; idx < contentXmlPartsLength; idx++) {
        await this.DeleteFromXMLStorage(contentXMLParts[idx]);
      }
    } catch (e) {
      throw new DocumentStorageError(DocumentStorageErrors.DeleteContentFromClientStorageFailed, ErrorStringFromException(e));
    }

    /* TODO: Empty string write is not allowed through web clinet.
    We will ask china team to fix the API to write empty content. So that we can avoid creation/deletion of objects again and agian. */
    // try {

    //   /* in case transcription content came from Legacy storage, 'IsTranscriptionFetchedFromCustomProperties' will be true and content need
    //   to be deleted from legacy Storage after write to new storage is success. */
    //   if (HostAppSettings.IsSettingTrue(HostAppSettingName.IsTranscriptionFetchedFromCustomProperties)) {
    //     await new DocumentStorageWord().DeleteTranscribeContentFromClient();
    //     HostAppSettings.Set(HostAppSettingName.IsTranscriptionFetchedFromCustomProperties, false);
    //   }

    //   await this.SetContentToXMLPart(await this.FetchCustomXMLPart(), EMPTY_STRING);
    // } catch (e) {
    //   throw new DocumentStorageError(DocumentStorageErrors.DeleteContentFromClientStorageFailed, ErrorStringFromException(e));
    // }
  }

  private async DeleteFromXMLStorage(contentXMLPart: Office.CustomXmlPart): Promise<void> {
    return new Promise<void>((resolve, reject) => {

      //Adding timeout incase of any 3rd party API causing incorrect behaviour.
      const deleteXMLPartsResultTimeout = setTimeout(() => {
        reject(new Error('deleteXMLPartsResultTimeout Timed Out'));
      }, MAX_MINUTES_WAIT_TIME_FOR_XML_STORAGE_OPERATION_MILLISECONDS /*timeoutInMs*/);

      const xmlDeleteCallback = function (xmlDeleteResult: Office.AsyncResult<void>) {

        clearTimeout(deleteXMLPartsResultTimeout);
        const status = !isNullOrUndefined(xmlDeleteResult) ? xmlDeleteResult.status : Office.AsyncResultStatus.Failed;
        switch (status) {
          case Office.AsyncResultStatus.Succeeded:
            resolve();
            break;
          case Office.AsyncResultStatus.Failed:
          default:
            const errorDetails = !isNullOrUndefined(xmlDeleteResult) ? ErrorStringFromException(xmlDeleteResult.error) : 'xmlDeleteResult is null or undefined';
            reject(new Error("unable to DeleteFromXMLStorage due to error " + errorDetails));
            break;
        }
      }

      //xmlDeleteCallback will be triggered as response of deleteAsync
      contentXMLPart.deleteAsync(xmlDeleteCallback);
    });
  }

  /*FetchCustomXMLPart: method This method help fetching the CustomXMLPart and create the NameSpaceURISchema if not present.
  return Promise<Office.CustomXmlPart> */
  private async FetchCustomXMLPart(): Promise<Office.CustomXmlPart> {
    return new Promise<Office.CustomXmlPart>(async (resolve, reject) => {
      const contentXMLParts: Office.CustomXmlPart[] = await this.getTranscriptionNamespacePart();

      if (!isNullOrUndefined(contentXMLParts) && contentXMLParts.length > 0 && !isNullOrUndefined(contentXMLParts[0])) {
        return resolve(contentXMLParts[0]);
      }

      // since no namespacePart is available, create NamespaceURI
      this.AddNamespaceURISchema()
        .then((contentXMLPart: Office.CustomXmlPart) => {
          if (!isNullOrUndefined(contentXMLPart)) {
            return resolve(contentXMLPart);
          }

          //reject the call as still contentXMLPart is null.
          return reject(new Error('Unable to add new namespace URI in the XML storage for transcription'));
        })
        .catch((e) => {
          //reject the call as still contentXMLPart is null.
          return reject(e);
        });
    });
  }

  private async getTranscriptionNamespacePart(): Promise<Office.CustomXmlPart[]> {
    return new Promise<Office.CustomXmlPart[]>((resolve, reject) => {

      /*Adding timeout incase of any 3rd party API causing incorrect behaviour.
        Resolving on Timeout current API is not working properly.
        After resolution this will be updated to reject*/
      const nameSpaceResponseTimeout = setTimeout(() => {
        resolve([]);
      }, MAX_MINUTES_WAIT_TIME_FOR_XML_STORAGE_GET_NAMESPACE_PART_MILLISECONDS /*timeoutInMs*/);

      const getByNamespaceCallback = (getByNamespaceResult: Office.AsyncResult<Office.CustomXmlPart[]>) => {
        const status = !isNullOrUndefined(getByNamespaceResult) ? getByNamespaceResult.status : Office.AsyncResultStatus.Failed;

        // clear response wait timer as response is received.
        clearTimeout(nameSpaceResponseTimeout);

        switch (status) {
          case Office.AsyncResultStatus.Succeeded:
            resolve(getByNamespaceResult.value);
            break;
          case Office.AsyncResultStatus.Failed:
          default:
            const errorDetails = !isNullOrUndefined(getByNamespaceResult)
              ? ErrorStringFromException(getByNamespaceResult.error)
              : 'xmlDeleteResult is null or undefined';
            reject(
              new DocumentStorageError(
                DocumentStorageErrors.FetchContentXMLPartFromClientStorageFailed,
                'unable to getByNamespaceAsync due to error ' + errorDetails
              )
            );
            break;
        }
      };

      //getByNamespaceCallback will be triggered as response of getByNamespaceAsync
      Office.context.document.customXmlParts.getByNamespaceAsync(s_uri, getByNamespaceCallback);
    });
  }

  private async AddNamespaceURISchema(): Promise<Office.CustomXmlPart> {
    return new Promise<Office.CustomXmlPart>((resolve, reject) => {
      //Adding timeout incase of any 3rd party API causing incorrect behaviour.
      const addNameSpaceUriResultTimeout = setTimeout(async () => {

        const contentXMLParts: Office.CustomXmlPart[] = await this.getTranscriptionNamespacePart();

        if (!isNullOrUndefined(contentXMLParts) && contentXMLParts.length > 0 && !isNullOrUndefined(contentXMLParts[0])) {
          LogError(LogEventNames.Warning, 'XMLSTORAGE : AddNamespaceURISchema timedout but customXmlParts created');
          resolve(contentXMLParts[0]);
        } else {
          reject(new Error('AddNamespaceURISchema Timed Out'));
        }
      }, MAX_MINUTES_WAIT_TIME_FOR_XML_STORAGE_OPERATION_MILLISECONDS /*timeoutInMs*/);

      const addNameSpaceUriCallback = function (addNamespaceUriResult: Office.AsyncResult<Office.CustomXmlPart>) {
        // clear response wait timer as response is received.
        clearTimeout(addNameSpaceUriResultTimeout);

        const status = !isNullOrUndefined(addNamespaceUriResult) ? addNamespaceUriResult.status : Office.AsyncResultStatus.Failed;

        switch (status) {
          case Office.AsyncResultStatus.Succeeded:
            resolve(addNamespaceUriResult.value);
            break;
          case Office.AsyncResultStatus.Failed:
          default:
            const errorDetails = !isNullOrUndefined(addNamespaceUriResult)
              ? ErrorStringFromException(addNamespaceUriResult.error)
              : 'addNamespaceResult is null or undefined';
            reject(new DocumentStorageError(DocumentStorageErrors.CorruptTranscriptSource, 'unable to AddNamespaceURI due to error ' + errorDetails));
            break;
        }
      };

      //addNameSpaceUriCallback will be triggered as response of addAsync
      Office.context.document.customXmlParts.addAsync(s_nameSpaceUriSchema, addNameSpaceUriCallback);
    });
  }

  private async SetContentToXMLPart(contentXMLPart: Office.CustomXmlPart, content: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      //Adding timeout incase of any 3rd party API causing incorrect behaviour.
      const setContentToXMLPartTimeout = setTimeout(() => {
        reject(new Error('setContentToXMLPart Timed Out'));
      }, MAX_MINUTES_WAIT_TIME_FOR_XML_STORAGE_OPERATION_MILLISECONDS /*timeoutInMs*/);

      const getNodecallback = function (result: Office.AsyncResult<Office.CustomXmlNode[]>) {
        if (isNullOrUndefined(result) || isNullOrUndefined(result.value)) {
          reject(new Error('getNodesAsync result/result.value is null or undefined'));
        }

        if (result.value.length != 1) {
          LogError(LogEventNames.Warning, 'extracted nodelist via SetContentToXMLPart has multiple node');
        }

        const xmlNode = result.value[0];
        /* TODO:
        Need to verify in Web Feature as currently this API is not working in web
        because "text" field is missing from CustomXmlPartsKeys {Id, Namespace, Xml, XPath, Prefix}.
        Discussion is going on with "Xiangmin Ru".*/

        const setTextAsyncCallback = function (setXMLResult: Office.AsyncResult<void>) {
          // clear response wait timer as response is received.
          clearTimeout(setContentToXMLPartTimeout);

          const status = !isNullOrUndefined(setXMLResult) ? setXMLResult.status : Office.AsyncResultStatus.Failed;
          switch (status) {
            case Office.AsyncResultStatus.Succeeded:
              resolve();
              break;
            case Office.AsyncResultStatus.Failed:
            default:
              const errorDetails = !isNullOrUndefined(setXMLResult)
                ? ErrorStringFromException(setXMLResult.error)
                : 'setXMLResult is null or undefined';
              reject(new Error('setTextAsync failed with error ' + errorDetails));
              break;
          }
        };

        //setTextAsyncCallback will be triggered as response of getByNamespaceAsync
        xmlNode.setTextAsync(content, setTextAsyncCallback);
      };

      //getNodecallback will be triggered as response of getByNamespaceAsync
      contentXMLPart.getNodesAsync(s_xpathRootNodeXMLStorage, getNodecallback);
    });
  }
}
